/**
 * ConectarCanal — vincular un WhatsApp (y revisar el email) SIN salir de donde estés.
 *
 * Por qué existe: el wizard cortaba en el paso de envío con un link a `/activa/whatsapp`. El
 * operador se iba con los archivos cargados, el mapeo confirmado, el contexto elegido y los
 * mensajes generados… y al volver estaba de nuevo en el paso 1. La conexión es un requisito del
 * envío, no un trámite de otra sección: tiene que poder resolverse en el mismo formulario.
 *
 * El circuito (crear línea provisional → elegir método → código/QR → poll hasta "activa") es el
 * mismo que el de la página dedicada; acá está extraído para poder embeberlo.
 */

import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Smartphone, CheckCircle2, X, AlertTriangle, Mail, RefreshCw } from "lucide-react";
import { activaApi } from "../api";
import { etiquetaEstadoLinea } from "../utils/estadoLinea";

type Linea = {
  id: string; nombre: string; estado: string; numero?: string;
  qr?: string; pairCode?: string; lastError?: string; conectada?: boolean;
};

export function ConectarCanal({
  onCambio,
  compacto = false,
  canal = "ambos",
  seleccionable = false,
  onLineasSeleccionadas,
}: {
  /** se dispara cuando cambia el estado de conexión, para que el padre revalide */
  onCambio?: () => void;
  compacto?: boolean;
  /**
   * Qué canal mostrar. El wizard tiene un paso por canal —y sólo aparece el que la marca
   * eligió—, así que este componente tiene que poder rendir uno solo. "ambos" es el modo
   * histórico (el panel de error del envío, donde se muestra el estado completo).
   */
  canal?: "whatsapp" | "email" | "ambos";
  /** true = mostrar checkboxes para ELEGIR desde qué líneas sale la campaña (paso de conexión). */
  seleccionable?: boolean;
  /** reporta la selección al padre: `ids` de líneas elegidas y `todas` (nada excluido = todas). */
  onLineasSeleccionadas?: (sel: { ids: string[]; todas: boolean }) => void;
}) {
  const verWa = canal === "whatsapp" || canal === "ambos";
  const verEmail = canal === "email" || canal === "ambos";
  const [lines, setLines] = useState<Linea[]>([]);
  const [cuentas, setCuentas] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  /** línea recién creada y todavía sin vincular: si se cancela, se borra (no deja basura) */
  const [provisional, setProvisional] = useState("");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  /**
   * Líneas que el operador DESTILDÓ. Se guarda lo excluido (no lo elegido) a propósito: así "todas"
   * es el default sin tener que re-elegir, y una línea nueva que se conecta entra sola a la campaña
   * (no está excluida) — que es lo que uno espera al "sumar" un número.
   */
  const [excluidas, setExcluidas] = useState<Set<string>>(new Set());
  const poll = useRef<any>(null);

  const load = async () => {
    try {
      const [l, e] = await Promise.allSettled([activaApi.lines(), activaApi.emailAccounts()]);
      if (l.status === "fulfilled") setLines(l.value.data || []);
      if (e.status === "fulfilled") setCuentas(e.value.data || []);
      onCambio?.();
    } catch { /* backend abajo: el padre ya muestra su propio error */ }
  };

  useEffect(() => { load(); return () => clearInterval(poll.current); }, []);

  const startPoll = (lineId: string) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const r = await activaApi.getLine(lineId).catch(() => null);
      if (!r?.data) return;
      setActive(r.data);
      if (r.data.estado === "activa" || r.data.estado === "desconectada") {
        clearInterval(poll.current);
        if (r.data.estado === "activa") setProvisional("");
        await load();
        if (r.data.estado === "activa") setTimeout(() => setActive(null), 1800);
        // el código venció antes de vincular: se vuelve al selector con el motivo a la vista
        else setActive((a: any) => (a ? { ...a, estado: "elegir", qr: "", pairCode: "", lastError: r.data.lastError || "" } : a));
      }
    }, 1500);
  };

  const agregar = async () => {
    setLoading(true);
    try {
      const r = await activaApi.createLine("Línea " + (lines.length + 1));
      if (!r?.data?.id) return;
      setProvisional(r.data.id);
      clearInterval(poll.current);
      setActive({ ...r.data, estado: "elegir", qr: "", pairCode: "" });
      setPhone("");
    } finally { setLoading(false); }
  };

  const cancelar = async () => {
    clearInterval(poll.current);
    const id = active?.id;
    setActive(null);
    // una línea que nunca se vinculó no debe quedar como "sin vincular" en la lista
    if (id && provisional === id) { await activaApi.deleteLine(id).catch(() => {}); setProvisional(""); }
    await load();
  };

  const vincularPorCodigo = async (line: any) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setActive({ ...line, estado: "elegir", qr: "", pairCode: "", lastError: "Ingresá el número con código de país, ej: 5491154596266" });
      return;
    }
    setActive({ ...line, estado: "conectando", qr: "", pairCode: "" });
    await activaApi.pairLine(line.id, digits).catch(() => {});
    startPoll(line.id);
  };

  const vincularPorQr = async (line: any) => {
    setActive({ ...line, estado: "conectando", qr: "", pairCode: "" });
    await activaApi.connectLine(line.id).catch(() => {});
    startPoll(line.id);
  };

  const conectadas = lines.filter((l) => l.conectada);
  const smtpListo = cuentas.some((c: any) => c.verificada);

  // selección efectiva = conectadas que NO están excluidas. "todas" cuando no se excluyó ninguna
  // conectada (así el envío puede mandar `undefined` = todas, e incluir cualquier línea recién sumada).
  const seleccionadas = conectadas.filter((l) => !excluidas.has(l.id));
  const todasElegidas = seleccionadas.length === conectadas.length;
  const toggleLinea = (id: string) =>
    setExcluidas((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // se reporta al padre SÓLO cuando la selección cambia de verdad (no en cada render): la key evita
  // el loop de pasar una función inline como dependencia del efecto.
  const selKey = seleccionadas.map((l) => l.id).join(",") + "|" + todasElegidas;
  useEffect(() => {
    if (seleccionable) onLineasSeleccionadas?.({ ids: seleccionadas.map((l) => l.id), todas: todasElegidas });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, seleccionable]);

  return (
    <div className="space-y-3">
      {/* ── Panel de vinculación en curso ─────────────────────────────────── */}
      {active ? (
        <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/40 p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-sm font-bold text-slate-900">Vinculá tu WhatsApp</p>
              <p className="text-xs text-slate-500 mt-0.5">Usá el WhatsApp del local, no uno personal.</p>
            </div>
            <button onClick={cancelar} className="text-slate-400 hover:text-slate-600" aria-label="Cancelar"><X size={18} /></button>
          </div>

          {active.lastError && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{active.lastError}</p>
            </div>
          )}

          {active.estado === "elegir" && (
            <div className="space-y-3">
              <div className="rounded-xl bg-white border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-700 mb-2">Opción 1 · Código de vinculación (recomendado)</p>
                <p className="text-[11px] text-slate-500 mb-2.5">El QR se renueva cada pocos segundos; el código dura más y es más fácil de tipear.</p>
                <div className="flex gap-2">
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="5491154596266"
                    inputMode="numeric"
                    className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <button onClick={() => vincularPorCodigo(active)} className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 whitespace-nowrap flex-shrink-0">
                    Obtener código
                  </button>
                </div>
              </div>
              <button onClick={() => vincularPorQr(active)} className="w-full h-10 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Opción 2 · Escanear QR
              </button>
            </div>
          )}

          {active.estado === "conectando" && !active.qr && !active.pairCode && (
            <div className="flex items-center gap-2.5 text-sm text-slate-600 py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Preparando la vinculación…
            </div>
          )}

          {active.pairCode && (
            <div className="text-center py-2">
              <p className="text-xs text-slate-500 mb-2">En tu teléfono: WhatsApp → Dispositivos vinculados → Vincular con número</p>
              <p className="text-3xl font-black tracking-[0.2em] text-violet-800 tabular-nums">{active.pairCode}</p>
            </div>
          )}

          {active.qr && (
            <div className="text-center py-2">
              <p className="text-xs text-slate-500 mb-2">WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
              <img src={active.qr} alt="Código QR para vincular WhatsApp" className="mx-auto w-48 h-48 rounded-xl border border-slate-200 bg-white" />
            </div>
          )}

          {active.estado === "activa" && (
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 py-4 justify-center">
              <CheckCircle2 size={18} /> ¡Listo! {active.numero} quedó conectado.
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ── Estado de los canales ───────────────────────────────────────── */}
          {verWa && (
          <div className="rounded-2xl border border-slate-100 bg-white p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Smartphone size={13} /> WhatsApp
              </p>
              <button onClick={agregar} disabled={loading} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50">
                {loading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Conectar un número
              </button>
            </div>

            {lines.length === 0 ? (
              <p className="text-sm text-slate-400">Todavía no hay ninguna línea. Sin al menos una, los mensajes de WhatsApp no salen.</p>
            ) : (
              <>
                {/* Cuando hay MÁS DE UNA conectada, se puede elegir desde cuáles sale la campaña.
                    Con una sola no hay nada que elegir: se muestra sin checkbox para no dar una
                    decisión falsa. */}
                {seleccionable && conectadas.length > 1 && (
                  <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                    <span className="text-slate-500">Sale desde <strong className="text-slate-700">{seleccionadas.length}</strong> de {conectadas.length} líneas</span>
                    <button onClick={() => setExcluidas(new Set())} disabled={todasElegidas} className="font-semibold text-violet-600 hover:text-violet-700 disabled:text-slate-300 disabled:cursor-default">
                      Seleccionar todas
                    </button>
                  </div>
                )}
                <div className="space-y-1">
                  {lines.map((l) => {
                    const elegible = seleccionable && l.conectada && conectadas.length > 1;
                    const elegida = !excluidas.has(l.id);
                    return (
                      <label key={l.id} className={`flex items-center gap-2.5 text-sm rounded-lg px-1.5 py-1 ${elegible ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                        {elegible ? (
                          <input type="checkbox" checked={elegida} onChange={() => toggleLinea(l.id)} className="w-4 h-4 rounded accent-violet-600 flex-shrink-0" aria-label={`Enviar desde ${l.nombre}`} />
                        ) : (
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${l.conectada ? "bg-emerald-500" : "bg-slate-300"}`} />
                        )}
                        <span className="font-medium text-slate-700 truncate">{l.nombre}</span>
                        <span className="text-xs text-slate-400 truncate">{l.conectada ? l.numero || "conectada" : etiquetaEstadoLinea(l.estado)}</span>
                        {!l.conectada && (
                          <button onClick={() => { clearInterval(poll.current); setActive({ ...l, estado: "elegir", qr: "", pairCode: "" }); setPhone(""); }} className="ml-auto text-xs font-semibold text-violet-600 hover:text-violet-700 flex-shrink-0">
                            Vincular
                          </button>
                        )}
                      </label>
                    );
                  })}
                </div>
                {/* si excluyó TODAS, no sale nada por WhatsApp: hay que decirlo, no dejar la cola en 0 en silencio */}
                {seleccionable && conectadas.length > 0 && seleccionadas.length === 0 && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                    No elegiste ninguna línea: así no sale ningún WhatsApp. Marcá al menos una.
                  </p>
                )}
              </>
            )}
          </div>
          )}

          {/* Email: no se configura acá (es un formulario aparte con credenciales SMTP), pero se
              DICE su estado — si falta, los mails se registran y no salen. */}
          {verEmail && !compacto && (
            <div className="rounded-2xl border border-slate-100 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <Mail size={13} /> Email
              </p>
              {smtpListo ? (
                <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={14} /> Hay una casilla verificada: los mails salen de verdad.</p>
              ) : (
                <p className="text-sm text-amber-800">
                  Sin casilla de email verificada. Los mensajes de email se van a <strong>registrar pero no enviar</strong>
                  {" "}(quedan marcados como "no salió"). El envío por WhatsApp no se ve afectado.
                </p>
              )}
            </div>
          )}

          <button onClick={load} className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1.5">
            <RefreshCw size={11} /> Actualizar estado
          </button>
        </>
      )}

      {verWa && conectadas.length > 0 && !active && (
        <p className="text-xs text-emerald-700 flex items-center gap-1.5">
          <CheckCircle2 size={13} />
          {seleccionable && conectadas.length > 1
            ? <>{seleccionadas.length} de {conectadas.length} línea{conectadas.length === 1 ? "" : "s"} elegida{seleccionadas.length === 1 ? "" : "s"} para enviar.</>
            : <>{conectadas.length} línea{conectadas.length === 1 ? "" : "s"} lista{conectadas.length === 1 ? "" : "s"} para enviar.</>}
        </p>
      )}
    </div>
  );
}
